/**
 * Created by neo on 10.01.2026
 * Challenges Tab - Displays active, upcoming, past challenges and impact summaries
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useLanguage } from '../../../Hooks/useLanguage';
import { useLocalized } from '../../../Hooks/useLocalized';
import { useLocalizedImage } from '../../../Hooks/useLocalizedMedia';
import { RouteChallenge } from '../../../Model/Engagement/RouteChallenge/RouteChallenge';
import { IndividualChallenge } from '../../../Model/Engagement/IndividualChallenge/IndividualChallenge';
import { AthleteTemplateImpactSummary } from '../../../Model/Engagement/Impact/MyImpact';
import { Theme } from '../../../Theme/Theme';
import { getMediaSmallest, getMediaMedium } from '../../../Model/Media/Media';

type ChallengeItem = RouteChallenge | IndividualChallenge;

export type ChallengesTabProps = {
  routeChallenges: RouteChallenge[];
  individualChallenges: IndividualChallenge[];
  pastRouteChallenges: RouteChallenge[];
  pastIndividualChallenges: IndividualChallenge[];
  impactSummaries: AthleteTemplateImpactSummary[];
};

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ChallengeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
`;

const ChallengeCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
`;

const ChallengeImage = styled.div<{ imageUrl?: string }>`
  height: 160px;
  background: ${(props) =>
    props.imageUrl
      ? `url(${props.imageUrl})`
      : `linear-gradient(135deg, ${Theme.Colors.primary} 0%, ${Theme.Colors.primaryDark} 100%)`};
  background-size: cover;
  background-position: center;
  position: relative;
`;

const ChallengeBadge = styled.span<{ variant: 'running' | 'upcoming' | 'ended' }>`
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(props) => {
    switch (props.variant) {
      case 'running':
        return Theme.Colors.success;
      case 'upcoming':
        return Theme.Colors.warning;
      case 'ended':
        return Theme.Colors.charcoal;
      default:
        return Theme.Colors.primary;
    }
  }};
  color: white;
`;

const ChallengeTypeBadge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  background: rgba(0, 0, 0, 0.5);
  color: white;
`;

const ChallengeContent = styled.div`
  padding: 16px;
`;

const ChallengeName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: ${Theme.Colors.textColor};
`;

const ChallengeDescription = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0 0 12px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ChallengeMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 13px;
  color: ${Theme.Colors.warmGrey};
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 24px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;

const EmptyEmoji = styled.span`
  font-size: 64px;
  display: block;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0;
`;

const PastChallengesToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 16px;
  background: white;
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.charcoal};
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${Theme.Colors.lightestGrey};
  }
`;

const SmallChallengeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const SmallChallengeCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const SmallChallengeImage = styled.div<{ imageUrl?: string }>`
  width: 60px;
  height: 60px;
  border-radius: 10px;
  flex-shrink: 0;
  background: ${(props) =>
    props.imageUrl
      ? `url(${props.imageUrl})`
      : `linear-gradient(135deg, ${Theme.Colors.primary} 0%, ${Theme.Colors.primaryDark} 100%)`};
  background-size: cover;
  background-position: center;
`;

const SmallChallengeContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SmallChallengeName = styled.h4`
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: ${Theme.Colors.textColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SmallChallengeMeta = styled.span`
  font-size: 12px;
  color: ${Theme.Colors.warmGrey};
`;

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ImpactCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 14px;
  border: 1px solid ${Theme.Colors.lighterGrey};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ImpactHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
`;

const ImpactEmoji = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${Theme.Colors.lightestGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const ImpactHeaderText = styled.div`
  flex: 1;
`;

const ImpactTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
`;

const ImpactPartner = styled.div`
  font-size: 12px;
  color: ${Theme.Colors.warmGrey};
  margin-top: 2px;
`;

const ImpactPartnerLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  object-fit: contain;
`;

const ImpactStats = styled.div`
  display: flex;
  align-items: center;
  background: ${Theme.Colors.lightestGrey};
  border-radius: 10px;
  padding: 12px;
`;

const ImpactStat = styled.div`
  flex: 1;
  text-align: center;
`;

const ImpactStatValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${Theme.Colors.primary};
`;

const ImpactStatLabel = styled.div`
  font-size: 11px;
  color: ${Theme.Colors.warmGrey};
  margin-top: 2px;
  text-transform: capitalize;
`;

const ImpactStatDivider = styled.div`
  width: 1px;
  height: 28px;
  background: ${Theme.Colors.lighterGrey};
  margin: 0 8px;
`;

// Extracted component to avoid hooks-in-loops
const ChallengeCardItem: React.FC<{
  challenge: ChallengeItem;
  onClick: (challenge: ChallengeItem) => void;
}> = observer(({ challenge, onClick }) => {
  const { t } = useTrans();
  const name = useLocalized(challenge.name);
  const description = useLocalized(challenge.description);
  const image = useLocalizedImage(challenge.images);
  const locationsCount = challenge.locations?.length ?? 0;

  const status: 'running' | 'upcoming' | 'ended' = challenge.ended
    ? 'ended'
    : challenge.started
      ? 'running'
      : 'upcoming';
  const type = 'locations' in challenge && challenge.locations ? 'Team' : 'Individual';

  return (
    <ChallengeCard onClick={() => onClick(challenge)}>
      <ChallengeImage imageUrl={image ? getMediaMedium(image) : undefined}>
        <ChallengeBadge variant={status}>
          {status === 'running'
            ? t('motivation.status.running', { defaultValue: 'Active' })
            : status === 'upcoming'
              ? t('motivation.status.upcoming', { defaultValue: 'Coming Soon' })
              : t('motivation.status.ended', { defaultValue: 'Ended' })}
        </ChallengeBadge>
        <ChallengeTypeBadge>{type}</ChallengeTypeBadge>
      </ChallengeImage>
      <ChallengeContent>
        <ChallengeName>{name}</ChallengeName>
        {description && <ChallengeDescription>{description}</ChallengeDescription>}
        <ChallengeMeta>
          <MetaItem>
            📍 {locationsCount} {t('motivation.locations', { defaultValue: 'locations' })}
          </MetaItem>
          <MetaItem>
            📅{' '}
            {status === 'running'
              ? t('motivation.endsIn', {
                  defaultValue: 'Ends {{date}}',
                  date: dayjs(challenge.endDateTime).fromNow(),
                })
              : t('motivation.startsIn', {
                  defaultValue: 'Starts {{date}}',
                  date: dayjs(challenge.startDateTime).fromNow(),
                })}
          </MetaItem>
        </ChallengeMeta>
      </ChallengeContent>
    </ChallengeCard>
  );
});

// Extracted component for small challenge cards
const SmallChallengeCardItem: React.FC<{
  challenge: ChallengeItem;
  onClick: (challenge: ChallengeItem) => void;
}> = observer(({ challenge, onClick }) => {
  const { t } = useTrans();
  const name = useLocalized(challenge.name);
  const image = useLocalizedImage(challenge.images);
  const endedDate = dayjs(challenge.endDateTime).format('MMM YYYY');

  return (
    <SmallChallengeCard onClick={() => onClick(challenge)}>
      <SmallChallengeImage imageUrl={image ? getMediaSmallest(image) : undefined} />
      <SmallChallengeContent>
        <SmallChallengeName>{name}</SmallChallengeName>
        <SmallChallengeMeta>
          {t('motivation.endedOn', { defaultValue: 'Ended {{date}}', date: endedDate })}
        </SmallChallengeMeta>
      </SmallChallengeContent>
    </SmallChallengeCard>
  );
});

// Extracted component for impact cards
const ImpactCardItem: React.FC<{
  summary: AthleteTemplateImpactSummary;
}> = observer(({ summary }) => {
  const { t } = useTrans();
  const { language } = useLanguage();
  const summaryName = useLocalized(summary.templateName);
  const formattedUnits = summary.totalUnits.toLocaleString(undefined, { maximumFractionDigits: 1 });
  const formattedCo2 = summary.totalCo2.toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <ImpactCard>
      <ImpactHeader>
        <ImpactEmoji>{summary.iconEmoji}</ImpactEmoji>
        <ImpactHeaderText>
          <ImpactTitle>{summaryName}</ImpactTitle>
          {summary.partnerName && <ImpactPartner>via {summary.partnerName}</ImpactPartner>}
        </ImpactHeaderText>
        {summary.partnerLogoUrl && <ImpactPartnerLogo src={summary.partnerLogoUrl} alt={summary.partnerName} />}
      </ImpactHeader>
      <ImpactStats>
        <ImpactStat>
          <ImpactStatValue>{formattedUnits}</ImpactStatValue>
          <ImpactStatLabel>{summary.getUnitName(language, summary.totalUnits)}</ImpactStatLabel>
        </ImpactStat>
        <ImpactStatDivider />
        <ImpactStat>
          <ImpactStatValue>{formattedCo2} kg</ImpactStatValue>
          <ImpactStatLabel>{t('impact.co2Offset', { defaultValue: 'CO₂ offset' })}</ImpactStatLabel>
        </ImpactStat>
        <ImpactStatDivider />
        <ImpactStat>
          <ImpactStatValue>{summary.campaignCount}</ImpactStatValue>
          <ImpactStatLabel>
            {summary.campaignCount === 1
              ? t('impact.challenge', { defaultValue: 'challenge' })
              : t('impact.challenges', { defaultValue: 'challenges' })}
          </ImpactStatLabel>
        </ImpactStat>
      </ImpactStats>
    </ImpactCard>
  );
});

export const ChallengesTab: React.FC<ChallengesTabProps> = observer(
  ({ routeChallenges, individualChallenges, pastRouteChallenges, pastIndividualChallenges, impactSummaries }) => {
    const { t } = useTrans();
    const navigate = useNavigate();
    const [showPastChallenges, setShowPastChallenges] = React.useState(false);

    const handleChallengeClick = React.useCallback(
      (challenge: ChallengeItem) => {
        navigate(`/motivation/challenge/${challenge.id}`);
      },
      [navigate],
    );

    const allChallenges = [...routeChallenges, ...individualChallenges].sort((a, b) => {
      const aRunning = a.started && !a.ended;
      const bRunning = b.started && !b.ended;
      if (aRunning !== bRunning) return aRunning ? -1 : 1;
      return a.startDateTime.getTime() - b.startDateTime.getTime();
    });

    const runningChallenges = allChallenges.filter((c) => c.started && !c.ended);
    const upcomingChallenges = allChallenges.filter((c) => !c.started && !c.ended);
    const pastChallenges = [...pastRouteChallenges, ...pastIndividualChallenges].sort(
      (a, b) => b.endDateTime.getTime() - a.endDateTime.getTime(),
    );

    if (allChallenges.length === 0 && pastChallenges.length === 0 && impactSummaries.length === 0) {
      return (
        <EmptyState>
          <EmptyEmoji>🏆</EmptyEmoji>
          <EmptyTitle>{t('motivation.empty.title', { defaultValue: 'No Challenges Available' })}</EmptyTitle>
          <EmptyDescription>
            {t('motivation.empty.description', {
              defaultValue: 'Check back later for new challenges to join!',
            })}
          </EmptyDescription>
        </EmptyState>
      );
    }

    return (
      <>
        {runningChallenges.length > 0 && (
          <Section>
            <SectionTitle>🏃 {t('motivation.sections.running', { defaultValue: 'Active Challenges' })}</SectionTitle>
            <ChallengeGrid>
              {runningChallenges.map((challenge) => (
                <ChallengeCardItem key={challenge.id} challenge={challenge} onClick={handleChallengeClick} />
              ))}
            </ChallengeGrid>
          </Section>
        )}

        {upcomingChallenges.length > 0 && (
          <Section>
            <SectionTitle>⏳ {t('motivation.sections.upcoming', { defaultValue: 'Coming Soon' })}</SectionTitle>
            <ChallengeGrid>
              {upcomingChallenges.map((challenge) => (
                <ChallengeCardItem key={challenge.id} challenge={challenge} onClick={handleChallengeClick} />
              ))}
            </ChallengeGrid>
          </Section>
        )}

        {pastChallenges.length > 0 && (
          <Section>
            <PastChallengesToggle onClick={() => setShowPastChallenges(!showPastChallenges)}>
              📜 {t('motivation.sections.pastChallenges', { defaultValue: 'Past Challenges' })} ({pastChallenges.length})
              <span>{showPastChallenges ? '▲' : '▼'}</span>
            </PastChallengesToggle>

            {showPastChallenges && (
              <SmallChallengeGrid>
                {pastChallenges.map((challenge) => (
                  <SmallChallengeCardItem key={challenge.id} challenge={challenge} onClick={handleChallengeClick} />
                ))}
              </SmallChallengeGrid>
            )}
          </Section>
        )}

        {impactSummaries.length > 0 && (
          <Section>
            <SectionTitle>🌱 {t('motivation.sections.myImpact', { defaultValue: 'My Impact' })}</SectionTitle>
            <ImpactGrid>
              {impactSummaries.map((summary) => (
                <ImpactCardItem key={summary.templateId} summary={summary} />
              ))}
            </ImpactGrid>
          </Section>
        )}
      </>
    );
  },
);
